import { apiClient, getToken } from '../const';
import { API_BASE_URL } from '../config/api';

const rawJson = { skipBodyWrap: true };
const multipart = {
  skipBodyWrap: true,
  headers: { 'Content-Type': 'multipart/form-data' },
};

export const WHATSAPP_FORBIDDEN_MESSAGE = "You don't have permission to configure WhatsApp";

const withQuery = (params) => {
  const cleaned = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    cleaned[key] = value;
  });
  return cleaned;
};

export function isApiSuccess(body) {
  if (!body || typeof body !== 'object') return false;
  if (body.success === true) return true;
  if (body.status === 'success') return true;
  if (body.message === 'radha') return true;
  return false;
}

export const isWhatsAppApiSuccess = isApiSuccess;

export function isWhatsAppForbiddenError(err) {
  return err?.response?.status === 403;
}

export function isWhatsAppSessionExpiredResponse(res) {
  return res?.sessionExpired === true;
}

export function unwrapWhatsAppList(res) {
  return unwrapList(res);
}

export function getWhatsAppErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  const mapped = mapWhatsAppApiError(err);
  return mapped || fallback;
}

export function isWhatsAppConnected(settings) {
  if (!settings || typeof settings !== 'object') return false;
  if (settings.configured === true) return true;
  if (String(settings.status || '').toLowerCase() === 'connected') return true;
  if (settings.hasAccessToken === true) return true;
  return false;
}

export function normalizeWhatsAppSettings(body) {
  if (!body || typeof body !== 'object') return null;
  const nested = body.data;
  if (
    nested
    && typeof nested === 'object'
    && ('configured' in nested || 'phoneNumberId' in nested || 'phone_number_id' in nested || 'status' in nested)
  ) {
    return {
      ...nested,
      phoneNumberId: nested.phoneNumberId ?? nested.phone_number_id ?? null,
      wabaId: nested.wabaId ?? nested.waba_id ?? null,
      appId: nested.appId ?? nested.app_id ?? null,
      apiVersion: nested.apiVersion ?? nested.api_version ?? 'v21.0',
      hasAccessToken: nested.hasAccessToken ?? nested.has_access_token,
      hasAppSecret: nested.hasAppSecret ?? nested.has_app_secret,
      hasWebhookVerifyToken: nested.hasWebhookVerifyToken ?? nested.has_webhook_verify_token,
    };
  }
  if ('configured' in body || 'phoneNumberId' in body || 'phone_number_id' in body || 'hasAccessToken' in body) {
    return {
      ...body,
      phoneNumberId: body.phoneNumberId ?? body.phone_number_id ?? null,
      wabaId: body.wabaId ?? body.waba_id ?? null,
      appId: body.appId ?? body.app_id ?? null,
      apiVersion: body.apiVersion ?? body.api_version ?? 'v21.0',
      hasAccessToken: body.hasAccessToken ?? body.has_access_token,
      hasAppSecret: body.hasAppSecret ?? body.has_app_secret,
      hasWebhookVerifyToken: body.hasWebhookVerifyToken ?? body.has_webhook_verify_token,
    };
  }
  return null;
}

export function mapWhatsAppApiError(err) {
  const status = err?.response?.status;
  if (status === 403) {
    return err?.response?.data?.message || WHATSAPP_FORBIDDEN_MESSAGE;
  }
  const message = err?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (typeof err?.message === 'string' && err.message.trim()) return err.message.trim();
  return 'Something went wrong. Please try again.';
}

export function unwrapList(body) {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== 'object') return [];
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.items)) return body.items;
  if (Array.isArray(body.chats)) return body.chats;
  if (Array.isArray(body.campaigns)) return body.campaigns;
  if (Array.isArray(body.templates)) return body.templates;
  if (Array.isArray(body.contacts)) return body.contacts;
  if (Array.isArray(body.messages)) return body.messages;
  if (body.data && typeof body.data === 'object') {
    const d = body.data;
    if (Array.isArray(d.data)) return d.data;
    if (Array.isArray(d.items)) return d.items;
    if (Array.isArray(d.chats)) return d.chats;
    if (Array.isArray(d.campaigns)) return d.campaigns;
    if (Array.isArray(d.templates)) return d.templates;
    if (Array.isArray(d.contacts)) return d.contacts;
    if (Array.isArray(d.messages)) return d.messages;
  }
  return [];
}

export function unwrapTotal(body, listLength = 0) {
  if (!body || typeof body !== 'object') return listLength;
  for (const c of [
    body.total,
    body.count,
    body.totalCount,
    body.data?.total,
    body.data?.count,
    body.meta?.total,
    body.pagination?.total,
  ]) {
    if (typeof c === 'number' && Number.isFinite(c)) return c;
  }
  return listLength;
}

export function unwrapData(body) {
  if (!body || typeof body !== 'object') return body;
  if (body.data !== undefined && typeof body.data === 'object' && !Array.isArray(body.data)) {
    return body.data;
  }
  return body;
}

// ── Settings ───────────────────────────────────────────────────────────

export const getSettings = async () => {
  const res = await apiClient.get('/api/whatsapp/settings');
  return normalizeWhatsAppSettings(res.data);
};

export const saveSettings = async (payload) => {
  const res = await apiClient.put('/api/whatsapp/settings', payload, rawJson);
  return normalizeWhatsAppSettings(res.data);
};

// ── Dashboard ──────────────────────────────────────────────────────────

export const getDashboard = async () => {
  const res = await apiClient.get('/api/whatsapp/dashboard');
  return unwrapData(res.data) ?? res.data;
};

// ── Campaigns ──────────────────────────────────────────────────────────

export const getCampaigns = async ({ page = 1, limit = 10, status = '', search = '' } = {}) => {
  const res = await apiClient.get('/api/whatsapp/campaigns', {
    params: withQuery({ page, limit: Math.min(limit, 10), status, search }),
  });
  const list = unwrapList(res.data);
  return { list, total: unwrapTotal(res.data, list.length), raw: res.data };
};

export const createCampaign = async (payload) => {
  // Prefer snake_case per contract
  const body = {
    title: payload.title,
    template_name: payload.template_name ?? payload.templateName,
    template_language: payload.template_language ?? payload.templateLanguage ?? 'en',
    phonebook_id: payload.phonebook_id ?? payload.phonebookId,
    body_variables: payload.body_variables ?? payload.bodyVariables ?? [],
    header_variable: payload.header_variable ?? payload.headerVariable ?? undefined,
    button_variables: payload.button_variables ?? payload.buttonVariables ?? [],
    schedule_at: payload.schedule_at ?? payload.scheduleAt ?? null,
    timezone: payload.timezone ?? 'Asia/Kolkata',
  };
  Object.keys(body).forEach((k) => {
    if (body[k] === undefined) delete body[k];
  });
  const res = await apiClient.post('/api/whatsapp/campaigns', body, rawJson);
  return unwrapData(res.data) ?? res.data;
};

export const getCampaignById = async (id) => {
  const res = await apiClient.get(`/api/whatsapp/campaigns/${id}`);
  return unwrapData(res.data) ?? res.data;
};

export const deleteCampaign = async (id) => {
  const res = await apiClient.delete(`/api/whatsapp/campaigns/${id}`);
  return res.data;
};

export const exportCampaignCsv = async (id) => {
  const res = await apiClient.get(`/api/whatsapp/campaigns/${id}/export`, {
    responseType: 'blob',
  });
  return res.data;
};

// ── Templates ──────────────────────────────────────────────────────────

export const getTemplates = async ({
  status = '', limit = 10, page = 1, search = '',
} = {}) => {
  const res = await apiClient.get('/api/whatsapp/templates', {
    params: withQuery({ status, limit: Math.min(limit, 10), page, search }),
  });
  const list = unwrapList(res.data);
  return { list, total: unwrapTotal(res.data, list.length), raw: res.data };
};

export const createTemplate = async (payload) => {
  const res = await apiClient.post('/api/whatsapp/templates', payload, rawJson);
  return unwrapData(res.data) ?? res.data;
};

export const uploadTemplateMedia = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await apiClient.post('/api/whatsapp/templates/media', fd, multipart);
  const data = res.data;
  const handle = data?.handle || data?.h || data?.id || data?.data?.handle || data?.data?.h || data?.data?.id;
  return { ...unwrapData(data), handle };
};

export const deleteTemplate = async (name) => {
  const res = await apiClient.delete(`/api/whatsapp/templates/${encodeURIComponent(name)}`);
  return res.data;
};

// ── Phonebooks ─────────────────────────────────────────────────────────

export const getPhonebooks = async () => {
  const res = await apiClient.get('/api/whatsapp/phonebooks');
  const list = unwrapList(res.data);
  return { list, raw: res.data };
};

export const createPhonebook = async (payload) => {
  const res = await apiClient.post('/api/whatsapp/phonebooks', payload, rawJson);
  return unwrapData(res.data) ?? res.data;
};

export const deletePhonebook = async (id) => {
  const res = await apiClient.delete(`/api/whatsapp/phonebooks/${id}`);
  return res.data;
};

export const getPhonebookContacts = async (id, { search = '', limit = 50, offset = 0 } = {}) => {
  const res = await apiClient.get(`/api/whatsapp/phonebooks/${id}/contacts`, {
    params: withQuery({ search, limit, offset }),
  });
  const list = unwrapList(res.data);
  return { list, total: unwrapTotal(res.data, list.length), raw: res.data };
};

export const addPhonebookContact = async (id, payload) => {
  const res = await apiClient.post(`/api/whatsapp/phonebooks/${id}/contacts`, payload, rawJson);
  return unwrapData(res.data) ?? res.data;
};

export const importPhonebookContacts = async (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await apiClient.post(`/api/whatsapp/phonebooks/${id}/contacts/import`, fd, multipart);
  return unwrapData(res.data) ?? res.data;
};

export const updateContact = async (id, payload) => {
  const res = await apiClient.put(`/api/whatsapp/contacts/${id}`, payload, rawJson);
  return unwrapData(res.data) ?? res.data;
};

export const deleteContacts = async (ids) => {
  const res = await apiClient.delete('/api/whatsapp/contacts', {
    ...rawJson,
    data: { ids },
  });
  return res.data;
};

// ── Inbox / chats ──────────────────────────────────────────────────────

export const getChats = async ({ search = '', limit = 20, offset = 0 } = {}) => {
  const res = await apiClient.get('/api/whatsapp/chats', {
    params: withQuery({ search, limit, offset }),
  });
  const list = unwrapList(res.data);
  return { list, total: unwrapTotal(res.data, list.length), raw: res.data };
};

export const getChatById = async (chatId) => {
  const res = await apiClient.get(`/api/whatsapp/chats/${encodeURIComponent(chatId)}`);
  return unwrapData(res.data) ?? res.data;
};

export const getChatMessages = async (chatId, { limit = 50, offset = 0 } = {}) => {
  const res = await apiClient.get(`/api/whatsapp/chats/${encodeURIComponent(chatId)}/messages`, {
    params: withQuery({ limit, offset }),
  });
  const list = unwrapList(res.data);
  return { list, total: unwrapTotal(res.data, list.length), raw: res.data };
};

export const markChatRead = async (chatId) => {
  const res = await apiClient.post(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/read`,
    {},
    rawJson,
  );
  return res.data;
};

export const sendChatText = async (chatId, text) => {
  const res = await apiClient.post(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/send-text`,
    { text },
    rawJson,
  );
  return unwrapData(res.data) ?? res.data;
};

export const sendChatImage = async (chatId, payload) => {
  const res = await apiClient.post(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/send-image`,
    payload,
    rawJson,
  );
  return unwrapData(res.data) ?? res.data;
};

export const sendChatVideo = async (chatId, payload) => {
  const res = await apiClient.post(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/send-video`,
    payload,
    rawJson,
  );
  return unwrapData(res.data) ?? res.data;
};

export const sendChatAudio = async (chatId, payload) => {
  const res = await apiClient.post(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/send-audio`,
    payload,
    rawJson,
  );
  return unwrapData(res.data) ?? res.data;
};

export const sendChatDocument = async (chatId, payload) => {
  const res = await apiClient.post(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/send-document`,
    payload,
    rawJson,
  );
  return unwrapData(res.data) ?? res.data;
};

export async function sendWhatsAppMediaMessage(chatId, type, { url, caption, filename } = {}) {
  if (type === 'image') return sendChatImage(chatId, { url, caption });
  if (type === 'video') return sendChatVideo(chatId, { url, caption });
  if (type === 'audio') return sendChatAudio(chatId, { url });
  if (type === 'document') return sendChatDocument(chatId, { url, caption, filename });
  throw new Error(`Unsupported media type: ${type}`);
}

export const sendChatTemplate = async (chatId, payload) => {
  const body = {
    template_name: payload.template_name ?? payload.templateName,
    template_language: payload.template_language ?? payload.templateLanguage ?? payload.language ?? 'en',
    body_variables: payload.body_variables ?? payload.bodyVariables ?? [],
  };
  const res = await apiClient.post(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/send-template`,
    body,
    rawJson,
  );
  return unwrapData(res.data) ?? res.data;
};

export const getChatNotes = async (chatId) => {
  // notes often embedded in chat detail; dedicated list may 404 — callers also use detail.notes
  try {
    const res = await apiClient.get(`/api/whatsapp/chats/${encodeURIComponent(chatId)}/notes`);
    return unwrapList(res.data);
  } catch {
    return [];
  }
};

export const createChatNote = async (chatId, { note, rating = 0 }) => {
  const res = await apiClient.post(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/notes`,
    { note, rating },
    rawJson,
  );
  return unwrapData(res.data) ?? res.data;
};

export const updateChatNote = async (chatId, noteId, { note, rating }) => {
  const res = await apiClient.put(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/notes/${noteId}`,
    { note, rating },
    rawJson,
  );
  return unwrapData(res.data) ?? res.data;
};

export const deleteChatNote = async (chatId, noteId) => {
  const res = await apiClient.delete(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/notes/${noteId}`,
  );
  return res.data;
};

export const clearChatNotes = async (chatId) => {
  const res = await apiClient.delete(`/api/whatsapp/chats/${encodeURIComponent(chatId)}/notes`);
  return res.data;
};

export const getChatContact = async (chatId) => {
  const res = await apiClient.get(`/api/whatsapp/chats/${encodeURIComponent(chatId)}/contact`);
  return unwrapData(res.data) ?? res.data;
};

export const saveChatContact = async (chatId, payload) => {
  const body = {
    phonebook_id: payload.phonebook_id ?? payload.phonebookId,
    name: payload.name,
    mobile: payload.mobile,
    var1: payload.var1,
    var2: payload.var2,
    var3: payload.var3,
    var4: payload.var4,
    var5: payload.var5,
  };
  Object.keys(body).forEach((k) => {
    if (body[k] === undefined || body[k] === '') delete body[k];
  });
  const res = await apiClient.post(
    `/api/whatsapp/chats/${encodeURIComponent(chatId)}/save-contact`,
    body,
    rawJson,
  );
  return unwrapData(res.data) ?? res.data;
};

export const getChatMedia = async (chatId, { type = 'all', limit = 40, offset = 0 } = {}) => {
  const res = await apiClient.get(`/api/whatsapp/chats/${encodeURIComponent(chatId)}/media`, {
    params: withQuery({ type, limit, offset }),
  });
  return unwrapList(res.data);
};

export const getChatMediaCounts = async (chatId) => {
  const res = await apiClient.get(`/api/whatsapp/chats/${encodeURIComponent(chatId)}/media/counts`);
  return unwrapData(res.data) ?? res.data;
};

export const uploadMedia = async (file, { convert = 'NO', onUploadProgress } = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  const convertFlag = convert === true || convert === 'YES' ? 'YES' : 'NO';
  fd.append('convert', convertFlag);
  const res = await apiClient.post('/api/whatsapp/media/upload', fd, {
    ...multipart,
    onUploadProgress: onUploadProgress
      ? (event) => {
        if (event.total) {
          onUploadProgress(Math.round((event.loaded * 100) / event.total));
        } else {
          onUploadProgress(null);
        }
      }
      : undefined,
  });
  const data = unwrapData(res.data) ?? res.data;
  return data;
};

/** Alias matching JewelleryXGuru naming; convert may be boolean. */
export const uploadWhatsAppMedia = async (file, { convert = false, onUploadProgress } = {}) => (
  uploadMedia(file, { convert: convert ? 'YES' : 'NO', onUploadProgress })
);

export const getMediaProxyUrl = (mediaId) => {
  const token = getToken();
  const base = `${API_BASE_URL}/api/whatsapp/media/${encodeURIComponent(mediaId)}`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
};

// JewelleryXGuru-compatible aliases (return API-shaped bodies for unwrapWhatsAppList)
export const listPhonebooks = async () => {
  const { raw, list } = await getPhonebooks();
  return raw ?? { data: list, success: true };
};

export const listChats = async (opts) => {
  const { raw, list, total } = await getChats(opts);
  return raw ?? { data: list, total, success: true };
};

export const listChatMessages = async (chatId, opts) => {
  const { raw, list, total } = await getChatMessages(chatId, opts);
  return raw ?? { data: list, total, success: true };
};

export const listChatMedia = async (chatId, opts) => {
  const list = await getChatMedia(chatId, opts);
  return { data: list, success: true };
};

export const listTemplates = async (opts) => {
  const { raw, list, total } = await getTemplates(opts);
  return raw ?? { data: list, total, success: true };
};

export const sendWhatsAppImage = sendChatImage;
export const sendWhatsAppVideo = sendChatVideo;
export const sendWhatsAppAudio = sendChatAudio;
export const sendWhatsAppDocument = sendChatDocument;
export const listMessages = listChatMessages;
export const sendTextMessage = sendChatText;
export const sendTemplateMessage = sendChatTemplate;
export const getChatDetail = getChatById;
